import React from "react";
import { ChangeEvent, Fragment } from "react";
import { Link, Prompt } from "react-router-dom";
import { RouteComponentPropsHistory } from "../../types";

import { apiGet, apiPatch, apiPost, V1 } from "../../Api";
import { filterStateCode, formatDateTimeLocal, formatName, formatTelephonePartial, formatZipCode, validateEmailOptional, validatePhoneNumber, validateStateCode } from "../../Utilities";
import HasRight from "../HasRight";
import withAuth from "../withAuth";
import withTitle from "../withTitle";

import { IAuthProps } from "../AuthContext";
import FileUploadDialog from "../FileUploadDialog";
import FileViewDialog from "../FileViewDialog";
import NoteBox from "../NoteBox";
import TabControl from "../TabControl";
import ValidateInput from "../ValidateInput";
import ViewToolbar from "../ViewToolbar";

import "../../blocks.css";
// import "../../icomoon.css";
// import "../../indicator.css";
// import "../../list.css";
import "../../spinner.css";
// import "../../table.css";
import "../../user.css";
import "../viewer.css";
import OverflowMenuItem from "../OverflowMenuItem";
import ToolbarMenuItem from "../ToolbarMenuItem";
import DeletionConfirmationDialog from "../DeletionConfirmationDialog";

interface IFairLocationDetailsState {
    error?: string;
    isEditingNote: boolean;
    isDeleteDialogOpen: boolean;
    isLoading: boolean;
    location?: any;

    employeeCount: number | undefined;
    name: string;
    insurance: string;
    insuranceFileId?: string;
    status: string;

    street: string;
    city: string;
    state: string;
    zip: string;

    contactName: string;
    contactEmail: string;
    contactPhone: string;

    isSubmitting: boolean;
    submitError?: string;
    hasChanged: boolean;
    noteBody: string;
    notes: any[];

    fairNotes: any[] | true | string;

    isUploadOpen: boolean;
    isViewerOpen: boolean;
}


class FairLocationDetailsView extends React.Component<RouteComponentPropsHistory<any, any> & IAuthProps, IFairLocationDetailsState> {
    private inputs: ValidateInput[];

    constructor(props: Readonly<RouteComponentPropsHistory<any, any> & IAuthProps>) {
        super(props);

        this.state = {
            isEditingNote: false,
            isDeleteDialogOpen: false,
            isLoading: true,

            employeeCount: undefined,
            insurance: "",
            name: "",
            status: "",

            city: "",
            state: "",
            street: "",
            zip: "",

            contactEmail: "",
            contactName: "",
            contactPhone: "",

            hasChanged: false,
            isSubmitting: false,
            noteBody: "",
            notes: [],

            fairNotes: [],

            isUploadOpen: false,
            isViewerOpen: false,
        };

        this.submitChanges = this.submitChanges.bind(this);
        this.cancelChanges = this.cancelChanges.bind(this);
        this.handleInputChanged = this.handleInputChanged.bind(this);
        this.handleRef = this.handleRef.bind(this);
        this.submitNote = this.submitNote.bind(this);
        this.handleTabChanged = this.handleTabChanged.bind(this);
        this.noteEditorChange = this.noteEditorChange.bind(this);
        this.showInsuranceUploadDialog = this.showInsuranceUploadDialog.bind(this);
        this.closeInsuranceUploadDialog = this.closeInsuranceUploadDialog.bind(this);
        this.handleUploadSuccess = this.handleUploadSuccess.bind(this);
        this.openViewer = this.openViewer.bind(this);
        this.closeViewer = this.closeViewer.bind(this);
        this.openDeleteDialog = this.openDeleteDialog.bind(this);
        this.closeDeleteDialog = this.closeDeleteDialog.bind(this);
        this.deleteCompany = this.deleteCompany.bind(this);
        this.postDeleteCompany = this.postDeleteCompany.bind(this);

        this.inputs = [];
    }

    public async componentDidMount() {
        const locationId = (this.props.match && this.props.match.params.id);

        try {
            if (locationId) {
                // Load existing location
                const location = await apiGet<any>(`fairlocations/${locationId}`);
                if (location.notes) {
                    location.notes.forEach((a: any) => a.author = formatName(a.employee.name));
                }

                this.setState({
                    isLoading: false,
                    location,

                    city: location.address.city,
                    state: location.address.state,
                    street: location.address.street,
                    zip: location.address.zip,

                    employeeCount: location.employeeCount,
                    insurance: location.insurance,
                    insuranceFileId: location.insuranceFileId,
                    name: location.name,
                    status: location.status || "",

                    notes: location.notes,

                    contactEmail: location.contact.email,
                    contactName: location.contact.name,
                    contactPhone: location.contact.phone,
                });
            }
            else {
                this.setState({
                    isLoading: false
                });
            }
        }
        catch (e) {
            this.setState({
                error: e.message,
                isLoading: false
            });
        }
    }

    public render() {
        if (this.state.isLoading) {
            return (
                <div className="viewer-root">
                    <div className="spinner-center lds-dual-ring-big" />
                </div>
            );
        }

        if (this.state.error) {
            return (
                <div className="viewer-root">
                    <span className="error-text">{this.state.error}</span>
                </div>
            );
        }

        return (
            <div className="viewer-root with-actionbar input-form">
                <ViewToolbar>
                    <Link to={`/fairtargets/${this.props.match.params.id}/fairs`} title="View company's fairs..." className="icon-list-ul" />
                    {
                        this.state.location ? (
                            <HasRight right="fair:create">
                                <Link to={{ pathname: "/fairs/add", state: { baseLocation: this.state.location, date: this.props.location.state && this.props.location.state.date } }} title="Create health fair for company..." className="icon-plus" />
                            </HasRight>
                        ) : ""
                    }
                    <OverflowMenuItem>
                        <ToolbarMenuItem right="lead:create">
                            <Link to={{ pathname: "/leads/new", state: { company: this.state.location } }}><i className="icon-plus" /><span>Create lead...</span></Link>
                        </ToolbarMenuItem>
                        <ToolbarMenuItem right="tco:merge">
                            <Link to={{ pathname: "/fairtargets/merge", state: { sourceId: this.props.match.params.id, name: this.state.name } }}><i className="icon-compress" /><span>Merge with...</span></Link>
                        </ToolbarMenuItem>
                        <ToolbarMenuItem right="tco:delete">
                            <button type="button" className="red" onClick={this.openDeleteDialog}><i className="icon-trash" /><span>Delete company</span></button>
                        </ToolbarMenuItem>
                    </OverflowMenuItem>
                </ViewToolbar>
                <Prompt when={this.state.hasChanged || this.state.isEditingNote} message="Your changes will not be saved if you continue." />
                <FileUploadDialog isOpen={this.state.isUploadOpen} onRequestClose={this.closeInsuranceUploadDialog} accept="image/png,image/jpeg" descriptionOverride="-" closeOnSuccess={true} endpoint={`fairlocations/${this.props.match.params.id}/insurance`} onUploadSuccess={this.handleUploadSuccess} />
                <FileViewDialog isOpen={this.state.isViewerOpen} fileId={this.state.insuranceFileId || ""} onRequestClose={this.closeViewer} />
                <DeletionConfirmationDialog isOpen={this.state.isDeleteDialogOpen} onRequestClose={this.closeDeleteDialog} onConfirm={this.deleteCompany} onSuccess={this.postDeleteCompany}>
                    <div>
                        This will delete the company notes, and proof of insurance as well.
                    </div>
                </DeletionConfirmationDialog>
                <div className="column-root">
                    <section className="info-block">
                        <header>
                            <h3>Basic Information</h3>
                        </header>
                        <section>
                            <div className="input-form-group">
                                <label htmlFor="frm-loc-name" className="required">Name</label>
                                <ValidateInput type="text" id="frm-loc-name" ref={this.handleRef} className="input-light" name="name" value={this.state.name} onChange={this.handleInputChanged} placeholder="Name" autoComplete="off_please_chrome" isRequired={true} />
                            </div>
                        </section>
                        <section>
                            <div className="input-form-group">
                                <label htmlFor="frm-loc-addr-street" className="required">Address line 1</label>
                                <ValidateInput type="text" id="frm-loc-addr-street" ref={this.handleRef} className="input-light" name="street" value={this.state.street} onChange={this.handleInputChanged} placeholder="Street Address" autoComplete="off_please_chrome" isRequired={true} />
                            </div>
                        </section>
                        <section>
                            <div className="input-form-group">
                                <label htmlFor="frm-loc-addr-city" className="required">City</label>
                                <ValidateInput type="text" id="frm-loc-addr-city" ref={this.handleRef} className="input-light" name="city" value={this.state.city} onChange={this.handleInputChanged} placeholder="City" autoComplete="off_please_chrome" isRequired={true} />
                            </div>
                        </section>
                        <section className="always-row">
                            <div className="input-form-group">
                                <label htmlFor="frm-loc-addr-state" className="required">State</label>
                                <ValidateInput type="text" id="frm-loc-addr-state" ref={this.handleRef} className="input-light" name="state" value={this.state.state} onChange={this.handleInputChanged} placeholder="State (e.g. GA)" autoComplete="off_please_chrome" isRequired={true} filter={filterStateCode} validator={validateStateCode} />
                            </div>
                            <div className="input-form-group input-form-group-small">
                                <label htmlFor="frm-loc-addr-zip" className="required">ZIP code</label>
                                <ValidateInput type="text" id="frm-loc-addr-zip" ref={this.handleRef} className="input-light" name="zip" value={this.state.zip} onChange={this.handleInputChanged} placeholder="5 digit ZIP" autoComplete="off_please_chrome" isRequired={true} filter={formatZipCode} validator={/^\d{5}$/} />
                            </div>
                        </section>
                        <section>
                            <div className="input-form-group">
                                <label htmlFor="frm-loc-status" className="required">Status</label>
                                <ValidateInput type="select" id="frm-loc-status" ref={this.handleRef} className="input-light" name="status" value={this.state.status} onChange={this.handleInputChanged} autoComplete="off_please_chrome" isRequired={true}>
                                    <option value="0" />
                                    <option value="1">OK to call</option>
                                    <option value="2">Do not call</option>
                                </ValidateInput>
                            </div>
                        </section>
                    </section>
                    <section className="info-block">
                        <header>
                            <h3>Additional Information</h3>
                        </header>

                        <section>
                            <div className="input-form-group">
                                <label htmlFor="frm-loc-employees">Number of employees</label>
                                <ValidateInput type="number" id="frm-loc-employees" ref={this.handleRef} className="input-light" name="employeeCount" value={this.state.employeeCount} onChange={this.handleInputChanged} min={0} max={100000} autoComplete="off_please_chrome" isRequired={false} />
                            </div>
                        </section>
                        <section>
                            <div className="input-form-group">
                                <label htmlFor="frm-loc-insurance">Primary Insurance</label>
                                <ValidateInput type="text" id="frm-loc-insurance" ref={this.handleRef} className="input-light" name="insurance" value={this.state.insurance} onChange={this.handleInputChanged} placeholder="Company-provided insurance provider" autoComplete="off_please_chrome" isRequired={false} />
                            </div>
                        </section>
                        <section>
                            <div className="input-form-group">
                                <label htmlFor="frm-loc-insurance-proof">Proof of insurance</label>
                                {
                                    !this.state.insuranceFileId
                                        ? <div className="error-box">No proof of insurance.</div>
                                        : null
                                }
                                <div className="action-panel-dialog">
                                    {this.state.insuranceFileId ? <button className="button text-with-icon" onClick={this.openViewer}><i className="icon-search" /><span>View</span></button> : null}
                                    <button className="button text-with-icon" onClick={this.showInsuranceUploadDialog}><i className="icon-upload" /><span>Upload</span></button>
                                </div>
                            </div>
                        </section>
                    </section>
                </div>
                &nbsp;
                <div className="column-root">
                    <section className="info-block">
                        <header>
                            <h3>Contact Info</h3>
                        </header>
                        <section>
                            <div className="input-form-group">
                                <label htmlFor="frm-loc-contact-name" className="required">Name</label>
                                <ValidateInput type="text" id="frm-loc-contact-name" ref={this.handleRef} className="input-light" name="contactName" value={this.state.contactName} onChange={this.handleInputChanged} placeholder="Name of contact person" autoComplete="off_please_chrome" isRequired={true} />
                            </div>
                        </section>
                        <section>
                            <div className="input-form-group">
                                <label htmlFor="frm-loc-contact-phone" className="required">Phone number</label>
                                <ValidateInput type="text" id="frm-loc-contact-phone" ref={this.handleRef} className="input-light" name="contactPhone" value={this.state.contactPhone} onChange={this.handleInputChanged} placeholder="(xxx) xxx-xxxx" autoComplete="off_please_chrome" filter={formatTelephonePartial} isRequired={true} requiredMessage="Phone number is required" validator={validatePhoneNumber} />
                            </div>
                            <div className="input-form-group">
                                <label htmlFor="frm-loc-contact-email">Email Address</label>
                                <ValidateInput type="text" id="frm-loc-contact-email" ref={this.handleRef} className="input-light" name="contactEmail" value={this.state.contactEmail} onChange={this.handleInputChanged} placeholder="Contact email (optional)" autoComplete="off_please_chrome" validator={validateEmailOptional} />
                            </div>
                        </section>
                    </section>
                    <section className="info-block">
                        <header>
                            <h3>Notes</h3>
                        </header>
                        <TabControl headers={["Company Notes", "Fair Notes"]} onTabChanged={this.handleTabChanged}>
                            <div>
                                {
                                    !this.state.location
                                        ? <textarea className="input-textarea-medium" name="noteBody" value={this.state.noteBody} onChange={this.handleInputChanged} placeholder="Enter company-specific note here..." />
                                        : <NoteBox displayName={this.props.authContext.user.displayName} notes={this.state.notes} onNewNote={this.submitNote} hideControls={!this.props.authContext.hasRight("appt:write")} onEditorChange={this.noteEditorChange} />
                                }
                            </div>
                            <div>
                                {
                                    this.state.fairNotes
                                        ? this.state.fairNotes === true
                                            ? <div className="spinner spinner-center lds-dual-ring" />
                                            : typeof this.state.fairNotes === "string"
                                                ? <span className="error-text">{this.state.fairNotes}</span>
                                                : this.state.fairNotes.length === 0
                                                    ? <span className="small-text">No notes available</span>
                                                    : <NoteBox notes={this.state.fairNotes} hideControls={true} noteHeaderExtraRender={this.noteHeaderRender} />
                                        : <span className="small-text">No notes available</span>
                                }
                            </div>
                        </TabControl>
                    </section>
                </div>
                &nbsp;
                {this.state.submitError ? <span className="error-text">{this.state.submitError}</span> : ""}
                <div className="action-panel">
                    <button className="button blue btn-confirm push-right" disabled={this.state.isSubmitting} onClick={this.submitChanges}>Save</button>
                    <button className="button blue btn-cancel" disabled={this.state.isSubmitting} onClick={this.cancelChanges}>Cancel</button>
                </div>
            </div>
        );
    }

    private async submitChanges() {
        let allValid = true;
        for (const input of this.inputs) {
            allValid = input.validate() && allValid;
        }

        if (!allValid) {
            return;
        }

        try {
            if (this.state.location) {
                // Update existing location
                this.setState({
                    isSubmitting: true,
                    submitError: undefined
                });

                const update = {
                    address: {
                        city: this.state.city,
                        state: this.state.state,
                        street: this.state.street,
                        zip: this.state.zip
                    },
                    contact: {
                        email: this.state.contactEmail,
                        name: this.state.contactName,
                        phone: (this.state.contactPhone || "").replace(/[^0-9]/g, "")
                    },
                    employeeCount: this.state.employeeCount,
                    insurance: this.state.insurance,
                    name: this.state.name,
                    status: this.state.status,
                };

                await apiPatch(`fairlocations/${this.state.location.id}`, update);

                this.setState({
                    hasChanged: false,
                    isSubmitting: false,
                });
            }
            else {
                // Create new location
                const creationInfo = {
                    address: {
                        city: this.state.city,
                        state: this.state.state,
                        street: this.state.street,
                        zip: this.state.zip
                    },
                    contact: {
                        email: this.state.contactEmail,
                        name: this.state.contactName,
                        phone: (this.state.contactPhone || "").replace(/[^0-9]/g, "")
                    },
                    employeeCount: this.state.employeeCount,
                    insurance: this.state.insurance,
                    name: this.state.name,
                    status: this.state.status,
                    note: this.state.noteBody,
                };

                this.setState({
                    isSubmitting: true,
                    submitError: undefined
                });

                await apiPost("fairlocations", creationInfo);

                this.setState({
                    hasChanged: false,
                    isSubmitting: false,
                });
            }
            this.props.history.goBack();
        }
        catch (e) {
            this.setState({
                isSubmitting: false,
                submitError: e.message
            });
        }
    }

    private cancelChanges() {
        this.props.history.goBack();
    }

    private handleInputChanged(e: ChangeEvent<HTMLInputElement> | ChangeEvent<HTMLSelectElement> | ChangeEvent<HTMLTextAreaElement>) {
        const value = e.currentTarget.value;
        const name = e.currentTarget.name;

        this.setState({
            hasChanged: true,
            [name]: value
        } as any)
    }

    private handleRef(input: ValidateInput) {
        this.inputs.push(input);
    }

    private async submitNote(body: string) {
        const note = {
            body
        };

        const noteResult = await apiPost<any>(`fairlocations/${this.state.location.id}/notes`, note);
        noteResult.author = formatName(noteResult.employee.name);

        this.setState(s => {
            s.notes.push(noteResult);

            return {
                notes: s.notes,
            };
        });
    }

    private noteEditorChange(isEditing: boolean) {
        this.setState({
            isEditingNote: isEditing
        });
    }

    private async handleTabChanged(index: number) {
        if (index === 1) {
            // Load fair notes
            try {
                this.setState({
                    fairNotes: true
                });

                const notes = this.state.location ? await apiGet<any[]>(`fairlocations/${this.state.location.id}/fairnotes`) : [];
                notes.forEach(a => a.author = formatName(a.employee.name));
                notes.forEach(a => a.timestamp = a.scheduledDate);

                this.setState({
                    fairNotes: notes
                });
            }
            catch (e) {
                this.setState({
                    fairNotes: e.message
                });
            }
        }
    }

    private noteHeaderRender(note: any) {
        return <Fragment>
            Fair date: {formatDateTimeLocal(note.timestamp)}
            <Link to={`/fairs/${note.fairId}`} className="push-right">View Fair</Link>
        </Fragment>;
    }

    private showInsuranceUploadDialog() {
        this.setState({
            isUploadOpen: true
        });
    }

    private closeInsuranceUploadDialog() {
        this.setState({
            isUploadOpen: false
        });
    }

    private handleUploadSuccess(ids: string[]) {
        this.setState({
            insuranceFileId: ids[0]
        });
    }

    private openViewer() {
        this.setState({
            isViewerOpen: true
        });
    }

    private closeViewer() {
        this.setState({
            isViewerOpen: false
        });
    }

    private openDeleteDialog() {
        this.setState({
            isDeleteDialogOpen: true
        });
    }

    private closeDeleteDialog() {
        this.setState({
            isDeleteDialogOpen: false
        });
    }

    private async deleteCompany() {
        await V1.delete(`fairlocations/${this.props.match.params.id}`);
    }

    private postDeleteCompany() {
        this.setState({
            hasChanged: false
        }, () => this.cancelChanges());
    }
}

export default withTitle("Target Company Information", withAuth(FairLocationDetailsView));
